import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Pencil, Trash2 } from 'lucide-react';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { labelValueGridSx } from '~/components/Fields/LabeledValue';
import { useIsInObjectDetails } from './objectDetailsContext';

/**
 * How a properties list is laid out: `compact` for a hover card, with its
 * own title row, or `panel` for a side panel that names the dataset itself,
 * with wider columns and larger text.
 */
export type PropertiesVariant = 'compact' | 'panel';

interface PropertiesPanelProps {
    children: ReactNode;
    variant?: PropertiesVariant;
    titleId?: string;
    subtitle?: ReactNode;
    showTitle?: boolean;
    /** Opens the object's DICOM tags; the hover panel's route to them. */
    onViewDicom?: () => void;
    onRename?: () => void;
    onDelete?: () => void;
    /**
     * Identity block (UIDs, SOP class, references) rendered after the
     * clinical fields. A separate slot rather than more children because
     * children are laid out in a two-column label/value grid, and a
     * collapsible section has to span both.
     */
    identity?: ReactNode;
    /** Full-width section(s) rendered after the identity block. */
    trailing?: ReactNode;
    /**
     * `grid` lays children out as label/value pairs. `sections` stacks
     * full-width PropertySection blocks instead, for the panels whose
     * body is sections rather than pairs.
     */
    layout?: 'grid' | 'sections';
}

/**
 * The rename and delete controls of an object, shown wherever its
 * properties are: the hover panel's header row and the details dialog's
 * header. Renders nothing when neither action is offered.
 */
export function PropertiesActions({
    onRename,
    onDelete,
}: {
    onRename?: () => void;
    onDelete?: () => void;
}) {
    const intl = useIntl();
    return (
        <>
            {onRename && (
                <Tooltip title={intl.formatMessage({ id: 'rename' })}>
                    <IconButton
                        size="small"
                        data-testid="properties-rename-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRename();
                        }}
                        sx={{ p: 0.25 }}
                        aria-label={intl.formatMessage({ id: 'rename' })}
                    >
                        <Pencil size={14} />
                    </IconButton>
                </Tooltip>
            )}
            {onDelete && (
                <Tooltip title={intl.formatMessage({ id: 'delete' })}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        sx={{ p: 0.25 }}
                        aria-label={intl.formatMessage({ id: 'delete' })}
                    >
                        <Trash2 size={14} />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );
}

export default function PropertiesPanel({
    children,
    variant = 'compact',
    titleId = 'properties',
    subtitle,
    showTitle = true,
    onViewDicom,
    onRename,
    onDelete,
    identity,
    trailing,
    layout = 'grid',
}: PropertiesPanelProps) {
    // The details dialog and the side panel each name the object in their
    // own header, so this header row is the hover card's alone.
    const isInObjectDetails = useIsInObjectDetails();

    return (
        <Box>
            {showTitle && variant === 'compact' && !isInObjectDetails && (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                >
                    <Typography fontSize={12} fontWeight={500}>
                        <FormattedMessage id={titleId} />
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                        {onViewDicom && (
                            <Link
                                component="button"
                                underline="hover"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewDicom();
                                }}
                                sx={{ fontSize: 11, cursor: 'pointer' }}
                            >
                                <FormattedMessage id="dicom.tagViewer.showLink" />
                            </Link>
                        )}
                        <PropertiesActions
                            onRename={onRename}
                            onDelete={onDelete}
                        />
                    </Box>
                </Box>
            )}
            {subtitle}
            <Box
                sx={{
                    ...(layout === 'sections'
                        ? {
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1.5,
                              minWidth: 0,
                          }
                        : variant === 'panel'
                          ? {
                                ...labelValueGridSx,
                                gridTemplateColumns:
                                    'minmax(0, 150px) minmax(0, 1fr)',
                                gap: '6px 12px',
                            }
                          : labelValueGridSx),
                    // The section eyebrows (overline) keep their own size.
                    '& .MuiTypography-root:not(.MuiTypography-overline)':
                        variant === 'panel'
                            ? {
                                  fontSize: 12,
                                  lineHeight: 1.4,
                                  // The panel's columns are fixed, so a
                                  // long name or value wraps, keeping its
                                  // own line breaks, rather than running
                                  // into the next column.
                                  whiteSpace: 'pre-wrap',
                                  overflowWrap: 'anywhere',
                              }
                            : { fontSize: 11, lineHeight: 1.2 },
                }}
            >
                {children}
            </Box>
            {identity}
            {trailing}
        </Box>
    );
}
